import { createContext, memo, type ReactNode, useContext, useEffect, useRef } from 'react';
import { useStore } from 'zustand';

import { reportErrorToBugsnag } from '@air/utils-error';

import {
  createWorkspaceAgentGuidedVariantFlowStore,
  type WorkspaceAgentGuidedVariantFlowState,
  type WorkspaceAgentGuidedVariantFlowStore,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/createWorkspaceAgentGuidedVariantFlowStore';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { isDevOrTestStage } from '~/swr-hooks/utils';

const missingWorkspaceAgentGuidedVariantFlowStore = createWorkspaceAgentGuidedVariantFlowStore();

const PrivateWorkspaceAgentGuidedVariantStoreContext = createContext<WorkspaceAgentGuidedVariantFlowStore>(
  missingWorkspaceAgentGuidedVariantFlowStore,
);

export type PrivateWorkspaceAgentGuidedVariantProviderProps = {
  children: ReactNode;
  /** Closes the flow when the open thread changes. Tests that never switch threads pass `null`. */
  sessionId: string | null;
};

/** Owns one guided-variant store for this panel session. */
export const PrivateWorkspaceAgentGuidedVariantProvider = memo(
  ({ children, sessionId }: PrivateWorkspaceAgentGuidedVariantProviderProps) => {
    const storeRef = useRef<WorkspaceAgentGuidedVariantFlowStore>();

    if (!storeRef.current) {
      storeRef.current = createWorkspaceAgentGuidedVariantFlowStore();
    }

    const store = storeRef.current;
    const previousSessionIdRef = useRef(sessionId);
    const pendingSessionReset = useWorkspaceAgentPanelStore((state) => state.pendingSessionReset);

    useEffect(() => {
      if (previousSessionIdRef.current === sessionId) {
        return;
      }

      previousSessionIdRef.current = sessionId;
      store.getState().closeGuidedVariantFlow();
    }, [sessionId, store]);

    useEffect(() => {
      if (!pendingSessionReset) {
        return;
      }

      // Selection-bar Start task sits outside this tree and only flags pendingSessionReset.
      store.getState().closeGuidedVariantFlow();
    }, [pendingSessionReset, store]);

    return (
      <PrivateWorkspaceAgentGuidedVariantStoreContext.Provider value={store}>
        {children}
      </PrivateWorkspaceAgentGuidedVariantStoreContext.Provider>
    );
  },
);

PrivateWorkspaceAgentGuidedVariantProvider.displayName = 'PrivateWorkspaceAgentGuidedVariantProvider';

export const useWorkspaceAgentGuidedVariantFlowStore = <Selected,>(
  selector: (state: WorkspaceAgentGuidedVariantFlowState) => Selected,
): Selected => {
  const store = useContext(PrivateWorkspaceAgentGuidedVariantStoreContext);

  if (store === missingWorkspaceAgentGuidedVariantFlowStore) {
    const error = new Error(
      'useWorkspaceAgentGuidedVariantFlowStore must be used within PrivateWorkspaceAgentGuidedVariantProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      context: 'Workspace agent guided variant store not found',
      error,
    });
  }

  return useStore(store, selector);
};
